import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SettingsIcon, ShieldAlertIcon, SaveIcon, CheckIcon } from 'lucide-react';
import { AfterTideLayout } from '../../components/AfterTide/AfterTideLayout';
import { useAfterTideUser } from '../../context/AfterTideUserContext';
import { DarknessTier, ConsentFramework } from '../../data/aftertideTypes';
import { tierMetadata, aftertideBooks } from '../../data/aftertideBooksData';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export default function Preferences() {
  const {
    preferences,
    updatePreferences
  } = useAfterTideUser();
  const navigate = useNavigate();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [maxTier, setMaxTier] = useState<DarknessTier | null>(preferences.maxTier);
  const [excludedWarnings, setExcludedWarnings] = useState<string[]>(preferences.excludedWarnings);
  const [preferredConsent, setPreferredConsent] = useState<ConsentFramework[]>(preferences.preferredConsent);
  const [isSaved, setIsSaved] = useState(false);
  const availableWarnings = useMemo(() => {
    const warnings = new Set<string>();
    aftertideBooks.forEach(b => b.contentWarnings.forEach(cw => cw.items.forEach(w => warnings.add(w))));
    return Array.from(warnings).sort();
  }, []);
  const consentOptions: ConsentFramework[] = ['enthusiastic', 'dubious', 'somnophilia', 'mixed', 'non-consent'];
  const handleSave = () => {
    updatePreferences({
      maxTier,
      excludedWarnings,
      preferredConsent
    });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };
  const toggleWarning = (warning: string) => {
    setExcludedWarnings(prev => prev.includes(warning) ? prev.filter(w => w !== warning) : [...prev, warning]);
  };
  const toggleConsent = (consent: ConsentFramework) => {
    setPreferredConsent(prev => prev.includes(consent) ? prev.filter(c => c !== consent) : [...prev, consent]);
  };
  return <AfterTideLayout data-id="element-5102" data-mp="1s1ilj5iim4qk">
      <div className={`${dk.page} min-h-screen pb-24`} data-id="element-5103" data-mp="1e4dkxl1kly2p">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6" data-id="element-5104" data-mp="1e5wy32rxjt09">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center ${dk.textSecondary} hover:text-[#e2b04a] transition-colors text-sm font-medium`} data-id="element-5105" data-mp="1i6guu58lculv">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-5106" data-mp="2at1o5b8mngs2" />
            Back
          </button>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5107" data-mp="1x2gd28pf0b4q">
          <div className="flex items-center justify-between mb-8" data-id="element-5108" data-mp="2141ij8zmdn4w">
            <div className="flex items-center" data-id="element-5109" data-mp="2126zk07ws53g">
              <SettingsIcon className={`h-8 w-8 ${rt.textPrimary} mr-3`} data-id="element-5110" data-mp="2ot2wfbr81qhk" />
              <h1 className={`font-playfair text-4xl font-bold ${dk.textPrimary}`} data-id="element-5111" data-mp="2tldz2mdcjqdt">
                Reader Preferences
              </h1>
            </div>
            <button onClick={handleSave} className={`flex items-center px-6 py-2.5 rounded-lg font-bold transition-all ${isSaved ? 'bg-green-600 text-white' : rt.primaryButton}`} data-id="element-5112" data-mp="d6shst6c3747">
              {isSaved ? <CheckIcon className="h-5 w-5 mr-2" data-id="element-5113" data-mp="mx3v36bwqsnr" /> : <SaveIcon className="h-5 w-5 mr-2" data-id="element-5114" data-mp="3vbly2tmzri0i" />}
              {isSaved ? 'Saved!' : 'Save Preferences'}
            </button>
          </div>

          <p className={`${dk.textSecondary} mb-10 text-lg`} data-id="element-5115" data-mp="3qforvg5fzs1g">
            Customize your AfterTide experience. These settings will help us
            tailor recommendations and warn you about content you prefer to
            avoid.
          </p>

          <div className="space-y-8" data-id="element-5116" data-mp="1kjb0gig1cp16">
            {/* Max Tier Setting */}
            <div className={`${dk.surface} border ${dk.border} rounded-xl p-6 md:p-8`} data-id="element-5117" data-mp="1flsoej6q1toy">
              <h2 className={`font-playfair text-2xl font-bold ${dk.textPrimary} mb-2`} data-id="element-5118" data-mp="3lal8j75cocjr">
                Maximum Intensity Tier
              </h2>
              <p className={`${dk.textSecondary} text-sm mb-6`} data-id="element-5119" data-mp="3u9q5wa56dzuc">
                Select the highest Darkness Tier you are comfortable reading. We
                won't recommend books above this tier.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3" data-id="element-5120" data-mp="81ymw3bn1qq3">
                {[1, 2, 3, 4, 5].map(tier => {
                const t = tier as DarknessTier;
                const info = tierMetadata[t];
                const isSelected = maxTier === t;
                return <button key={t} onClick={() => setMaxTier(maxTier === t ? null : t)} className={`p-4 rounded-lg border text-center transition-all ${isSelected ? info.badgeColor + ' ring-2 ring-offset-2 ring-offset-[#16213e] ring-[#e2b04a]' : `${dk.border} ${dk.page} ${dk.textSecondary} hover:border-[#e2b04a]/30`}`} data-id="element-5121" data-mp="ltd5wa5jprx5">
                      <div className="font-bold text-lg mb-1" data-id="element-5122" data-mp="10wod4tv265wo">T{t}</div>
                      <div className="text-xs uppercase tracking-wider" data-id="element-5123" data-mp="vxtm54mxtb1h">
                        {info.name}
                      </div>
                    </button>;
              })}
              </div>
              {maxTier && <p className={`mt-4 text-sm ${rt.textPrimary}`} data-id="element-5124" data-mp="1om8whdv4j56h">
                  You will only see recommendations for Tiers 1 through{' '}
                  {maxTier}.
                </p>}
            </div>

            {/* Consent Frameworks */}
            <div className={`${dk.surface} border ${dk.border} rounded-xl p-6 md:p-8`} data-id="element-5125" data-mp="1awf5oq026au4">
              <h2 className={`font-playfair text-2xl font-bold ${dk.textPrimary} mb-2`} data-id="element-5126" data-mp="27ebht2ipn9n2">
                Preferred Consent Frameworks
              </h2>
              <p className={`${dk.textSecondary} text-sm mb-6`} data-id="element-5127" data-mp="qaugjgff6q1">
                Select the consent dynamics you enjoy reading about. This helps
                us curate your Browse page.
              </p>

              <div className="flex flex-wrap gap-3" data-id="element-5128" data-mp="3n42nrz9fk6kh">
                {consentOptions.map(consent => {
                const isSelected = preferredConsent.includes(consent);
                return <button key={consent} onClick={() => toggleConsent(consent)} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors capitalize ${isSelected ? `${rt.accentSecondaryTintHover} border-[#c77dba] ${rt.accentSecondaryText}` : `${dk.page} ${dk.border} ${dk.textSecondary} hover:border-[#c77dba]/50`}`} data-id="element-5129" data-mp="3e3crkug9ce68">
                      {consent.replace('-', ' ')}
                    </button>;
              })}
              </div>
            </div>

            {/* Excluded Warnings */}
            <div className={`${dk.surface} border border-red-900/30 rounded-xl p-6 md:p-8`} data-id="element-5130" data-mp="2rw0ha68kftz8">
              <div className="flex items-center mb-2" data-id="element-5131" data-mp="31ljxt58t3bqx">
                <ShieldAlertIcon className="h-6 w-6 text-red-500 mr-2" data-id="element-5132" data-mp="31mbu6ia89di9" />
                <h2 className={`font-playfair text-2xl font-bold ${dk.textPrimary}`} data-id="element-5133" data-mp="2wuk1a13lmvoy">
                  Hard Limits (Excluded Content)
                </h2>
              </div>
              <p className={`${dk.textSecondary} text-sm mb-6`} data-id="element-5134" data-mp="36jlgvq68fb2e">
                Select specific content warnings you want to completely avoid.
                Books containing these tags will be hidden from your Browse
                results.
              </p>

              <div className="flex flex-wrap gap-2" data-id="element-5135" data-mp="1q6dhm4fuc9ca">
                {availableWarnings.map(warning => {
                const isExcluded = excludedWarnings.includes(warning);
                return <button key={warning} onClick={() => toggleWarning(warning)} className={`px-3 py-1.5 rounded text-xs transition-colors ${isExcluded ? 'bg-red-900/40 border border-red-500 text-red-200' : `${dk.page} border ${dk.border} ${dk.textSecondary} hover:border-red-500/30 hover:text-red-300`}`} data-id="element-5136" data-mp="zeeftqevw67e">
                      {warning}
                    </button>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AfterTideLayout>;
}